import { createBrowserHistory } from 'history';
import getInitials from 'initials';
import { merge } from 'lodash-es';
import storage from 'local-storage-fallback';
import inx from 'inx';
import { ATTACHMENTS, MAIL, DEFAULT_TITLE } from 'newJs/config';
import {useMailListStore} from 'state/mailListState';

const MSD = MAIL.get('SYSTEM_DIRS');
const { MODE_NAMES: CMN } = MAIL.get('COMPOSE');
const SYSTEM_FOLDERS = [MSD.INBOX, MSD.DRAFT, MSD.LISTS, MSD.SENT, MSD.OUTBOX, MSD.SOCIAL];

export const addPlus = (num) => (num > 99) ? '99+' : num;

// Func to use with React's dangerouslySetInnerHTML
export const createMarkup = (htmlString) => ({ __html: htmlString });

export const doAjax = (args) => {
    let opts = {
        xhrFields: {
            withCredentials: true
        },
        error: (xhr) => {
            //no_network: 0 - logout: 401 MAIL-5423 - filter_quota: 412, 406- mailbox already exists
            const excludedStatusCode = [0, 401, 412, 406];
            const excludedError = ['ZipException'];
            if (!excludedStatusCode.includes(xhr.status) && !excludedError.includes(xhr?.responseJSON?.error)) {
                console.error(`DoAjax error: code ${xhr.status} ${JSON.stringify(xhr)}`);
            }
        }
    };
    if (typeof (args) === 'object') {
        opts = { ...opts, ...args };
    }
    return $.ajax(opts).promise();
};

export const doFetch = async (opts) => {
    const { url, ...otherOpts } = opts;

    if (typeof url !== 'string') {
        throw new Error('URL must be a string');
    }
    if (url.length < 1) {
        throw new Error('URL must not be empty');
    }

    const defaults = { credentials: 'include' };

    return await fetch(url, { ...defaults, ...otherOpts });
};

export const checkTimeZone = (data) => data.timeZone?.length > 0
    ? data.timeZone
    : Intl.DateTimeFormat().resolvedOptions().timeZone; // get time zone from browser

export const convertToArrayOfIntegers = (list) => {
    if (Array.isArray(list)) {
        return list.map((item) => parseInt(item));
    }
    if (typeof list === 'string') {
        const parsed = parseInt(list);
        return !isNaN(parsed) && isFinite(parsed) ? [parsed] : [];
    }
    return [];
};

// Translate system folder names
export const folderTranslation = ((dirs) => (i18n) => {
    const dirKeys = Object.keys(dirs);
    const dirValues = Object.values(dirs);
    const i18nKeys = Object.keys(i18n);

    return (folderName) => {
        const nameIndex = dirValues.indexOf(folderName);

        if (nameIndex != -1) {
            const key = dirKeys[nameIndex];
            return i18nKeys.indexOf(key) != -1 ? i18n[key] : folderName;
        }
        return folderName;
    };
})(MSD);

export const getEmlIdFromLocation = (locationSearch) => {
    const regex = /[?&]index=(\d+)/i;
    const match = regex.exec(locationSearch);
    return match ? match[1] : 1;
};

export const getPageFromLocation = (searchString) => {
    const searchParams = new URLSearchParams(searchString);
    return searchParams.has('page') ? Number(searchParams.get('page')) : 1;
};

export const getFlag = (str) => {
    const toggle = !!(+str.slice(0, 1)); // Convert string to boolean
    const flag = str.slice(1).toUpperCase();

    return { flag, toggle };
};

export const getObjProps = (obj) => {
    return (prop) => obj.map(item => item[prop]);
};

export const hexToRgb = (hex) => {
    const
        bigint = parseInt(hex, 16),
        r = (bigint >> 16) & 255,
        g = (bigint >> 8) & 255,
        b = bigint & 255;

    return [r, g, b].join(',');
};

export const folderNames = ((DL) => ({
    isSpamFolder: (name) => name === DL.SPAM,
    isTrashFolder: (name) => name === DL.TRASH
}))(MSD);

export const isScreenSize = {
    small: (name) => ['xs', 'sm'].includes(name),
    lg: (name) => 'lg' === name,
    md: (name) => 'md' === name,
    sm: (name) => 'sm' === name,
    xs: (name) => 'xs' === name,
};

export const isScreen = ((obj) => (breakpointName) => {
    return Object.entries(obj).reduce((acc, [key, fn]) => {
        acc[key] = fn(breakpointName);
        return acc;
    }, {});
})(isScreenSize);

export const isSystemFolder = (name) => SYSTEM_FOLDERS.includes(name);

export const isPlayable = (item) => {
    if (!item.extension) return false;

    const { PLAYABLE } = ATTACHMENTS.FILE_ACTIONS;
    const extension = item?.extension?.length > 0
        ? item.extension.toLowerCase()
        : false;

    return PLAYABLE.includes(extension);
};

export const isViewable = (item) => {
    if (!item.extension) return false;

    const { SHOW_THUMBS_ONLY } = ATTACHMENTS.FILE_ACTIONS;
    const extension = item?.extension?.length > 0
        ? item.extension.toLowerCase()
        : false;

    return SHOW_THUMBS_ONLY.includes(extension)
        ? false
        : item.thumb ? true : false;
};

export const objHasProp = (prop, obj) => Object.prototype.hasOwnProperty.call(obj, prop);

export const parseEmlDates = (obj, lang, options) => {
    /* INFO:
        date, datetime - "The date this message was sent."
        receivedDate, receivedDateTime - "The date this message was received."
        https://jira.co.inbox.lv/browse/MAPI-537 for more info about misspelled key name
    */
    const { formats, timeZone } = options;
    const timeZoneDef = timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
    const namesList = ['datetime', 'recievedDateTime', 'receivedDateTime'];
    const formatsList = Object.keys(formats);

    const result = formatsList.map((formatName) => {
        // console.log(formatName);
        const dates = namesList.map((name) => {
            if (!Object.hasOwnProperty.call(obj, name)) return [name, null];

            const itemDate = new Date(obj[name]);
            return [name, itemDate.toLocaleString(lang, { timeZoneDef, ...formats[formatName] })];
        });

        return [formatName, Object.fromEntries(dates)];
    });

    // console.log(Object.fromEntries(result));
    return Object.fromEntries(result);
};

// Return mail flag's translations and names
export const parseMailFlagsList = (arr) => {
    const list = [];
    const t9n = {};

    for (const item of arr.slice(1)) {
        list.push(item[0]);
        t9n[item[0]] = item.slice(1);
    }

    return { list, t9n };
};

// Use prefix for localstorage keys to avoid data overwrite
export const prefixStorageKeys = (prefix = null) => {
    const getKey = (key) => prefix ? `${prefix}_${key}` : key;

    return {
        get: (key) => storage.getItem(getKey(key)),
        remove: (key) => storage.removeItem(getKey(key)),
        set: (key, value) => storage.setItem(getKey(key), value),
    };
};

export const returnModeFromPath = () => {
    const pathname = window.location.pathname.substring(1); // remove leading '/'
    if (Object.values(CMN).includes(pathname)) return pathname;

    return false;
};

// Append unread/recent email count to the document.title
export const setPageTitle = (val, type) => {
    let newTitle;
    const {translations} = inx.i18n;
    // set Premium mail or default
    const name = inx.params.mailType.isMailPlus ? translations.pageTitle.premium : translations.pageTitle.title;
    if (type === 'message') {
        newTitle = `${translations.pageTitle.msgView} - ${name}`;
    } else if (type === 'compose') {
        newTitle =`${val} - ${name}`;
    } else {
        newTitle = val > 0
            ? `${addPlus(val)} ${translations.pageTitle.unread} - ${name}`
            : `${name}`;
    }
    document.title = newTitle;
};

export const trimStartEnd = (arg) => {
    return typeof (arg) === 'string'
        ? arg.replace(/^[\\/$]+|\/$/g, '')
        : arg;
};

export const twoLetterInitials = (str) => {
    const initials = getInitials(str).toUpperCase();

    return initials.length > 2
        ? initials.slice(0, 1) + initials.slice(-1)
        : initials;
};

export const history = createBrowserHistory();

export const serialiseSearchObjectToString = (params, prefix) => {
    const query = Object.keys(params).map((key) => {
        const value = params[key];
        if (params.constructor === Array)
            key = `${prefix}`;
        else if (params.constructor === Object)
            key = (prefix ? `${prefix}[${key}]` : key);
        if (typeof value === 'object')
            return serialiseSearchObjectToString(value, key);
        if (value !== '' && value !== 'undefined')
            return `${key}=${encodeURIComponent(value)}`;
    });

    return [].concat.apply([], query).join('&');
};

export const serialiseParamsToSearchObject = (params) => {
    const searchParams = new URLSearchParams(params);
    const excludedParams = ['mailbox', 'array_index', 'index', 'page'];

    if (
        searchParams.has('type')
        || searchParams.has('keyword')
        || searchParams.has('inFolders')
    ) {
        const object = { inFolders: [] };

        searchParams.forEach((value, key) => {
            if (excludedParams.includes(key)) return;
            if (!key.includes('params') && value !== 'undefined' && value !== '') {
                key === 'inFolders'
                    ? merge(object, { [key]: [...object.inFolders, value] })
                    : merge(object, { [key]: value });
            }
            if (key.includes('params') && value !== 'undefined' && value !== '') {
                const params = key.split('[');
                const convertValue = value === 'true' || value === 'false' ? JSON.parse(value) : value;
                merge(object, { [params[0]]: { [params[1].slice(0, -1)]: convertValue } });
            }
        });

        return object;
    }

    return false;
};

export const updateSearchParam = (key, val) => {
    const winLoc = window.location;
    const searchParams = new URLSearchParams(winLoc.search);
    searchParams.set(key, val);
    return `${winLoc.pathname}?${searchParams.toString()}${winLoc.hash}`;
};

// FIXME after /prefs (options page with all other subpages) will be moved to react and could work with react router
export const isOptionsPageNavigation = () => {
    const optionsPageList = ['prefs', 'signature', 'filters', 'folders', 'labels', 'other'];
    return optionsPageList.some(path => window.location.pathname.includes(path));
};

// check if need call batch functionality to update messages
// FIXME: should refactor to use one functionality batch and single in requestDispatcher, changes also in MAPI
export const useBatchActions = (currentFolder) => {
    const search = useMailListStore((state) => state.search);

    return ['ALL', '**search'].includes(currentFolder)
        || search.inFolders.length > 1;
};

export const isOldChrome = () => {
    const userAgent = window.navigator.userAgent;
    const version = userAgent.split('Chrome/')[1];

    if(typeof version === 'undefined') return false;

    return version.split(' ')[0] < 66;
};

const fileTypesArr = [
    'archive',
    'audio',
    'database',
    'diskImage',
    'document',
    'email',
    'event',
    'excel',
    'file',
    'image',
    'link',
    'pdf',
    'presentation',
    'project',
    'sourceCode',
    'textDoc',
    'video',
    'webPage',
    'youtube',
    'text doc'
];

export const getFileType = (mimeType) => {
    const parts = mimeType.split('/');
    const fileType = parts[0];

    if (fileType === 'application') {
        const subParts = parts[1].split('.');
        return subParts[subParts.length - 1];
    }

    // Check if fileType is in the fileTypesArr array, otherwise default to 'file'
    return fileTypesArr.includes(fileType) ? fileType : 'file';
};

// Function to generate a custom unique ID
export const generateUniqueId = () => {
    const timestamp = Date.now().toString(36); // Convert timestamp to base-36 string
    const randomString = Math.random().toString(36).substring(2); // Generate a random string
    return timestamp + randomString; // Combine timestamp and random string
};